import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { firestore } from '../utils/firebaseSetUp';
import {
  Product,
  ProductCreate,
  ProductUpdate,
  ProductQueryResult,
} from '../models';
import type {
  ProfitPlus2k8ProductIntegration,
  WarehouseStockMap,
} from '../models/integrations/profitPlus2k8';
import type {
  ProductMarginStats,
  ProductNumericFieldStats,
  ProductSalesStats,
  ProductStats,
} from '../models/productStats';
import { normalizeWarehouseStockMap } from '../utils/profitPlus2k8Product';
import { authenticatedHttpClient } from './httpClient';
import { isSelfHosted } from '../config/infra';
import {
  createApiListCursor,
  getApiListOffset,
} from '../utils/apiListCursor';

/** Coerce profitPlus2k8.fields values to strings for Firestore + bridge (item, codes, etc.). */
function normalizeProfitFieldsForWrite(
  pp: ProfitPlus2k8ProductIntegration | undefined
): ProfitPlus2k8ProductIntegration | undefined {
  if (!pp?.fields || typeof pp.fields !== 'object') return pp;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(pp.fields as Record<string, unknown>)) {
    if (v === undefined || v === null) continue;
    out[k] = typeof v === 'string' ? v : String(v);
  }
  return { ...pp, fields: out as ProfitPlus2k8ProductIntegration['fields'] };
}

function extractProductApiError(error: unknown, fallback: string): string {
  const err = error as {
    response?: { data?: { detail?: string | Array<{ msg?: string }> } };
    message?: string;
  };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (first && typeof first.msg === 'string' && first.msg.trim()) {
      return first.msg;
    }
  }
  if (err.message?.trim()) return err.message;
  return fallback;
}

const SERVER_MANAGED_PRODUCT_FIELDS = [
  'id',
  'keywords',
  'stats',
  'created',
  'updated',
] as const;

function stripServerManagedProductFields(
  payload: ProductCreate | ProductUpdate
): void {
  const mutable = payload as unknown as Record<string, unknown>;
  for (const key of SERVER_MANAGED_PRODUCT_FIELDS) {
    delete mutable[key];
  }
}

function prepareProductCreatePayload(data: ProductCreate): ProductCreate {
  const payload: ProductCreate = { ...data };
  stripServerManagedProductFields(payload);
  if (payload.profitPlus2k8) {
    payload.profitPlus2k8 = normalizeProfitFieldsForWrite(payload.profitPlus2k8);
  }
  return payload;
}

function prepareProductUpdatePayload(data: ProductUpdate): ProductUpdate {
  const payload: ProductUpdate = { ...data };
  stripServerManagedProductFields(payload);
  if (payload.profitPlus2k8) {
    payload.profitPlus2k8 = normalizeProfitFieldsForWrite(payload.profitPlus2k8);
  }
  return payload;
}

function productFromApiResponse(data: Record<string, unknown>): Product {
  const id = typeof data.id === 'string' ? data.id : '';
  return productFromFirestoreData(id, data as DocumentData);
}

/** Normalize profitPlus2k8 from Firestore (syncedAt may be Timestamp). */
function parseProfitPlus2k8(
  raw: unknown
): ProfitPlus2k8ProductIntegration | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const fields = o.fields;
  let syncedAt: Date | null = null;
  if (o.syncedAt != null) {
    const t = o.syncedAt as { toDate?: () => Date };
    syncedAt = typeof t.toDate === 'function' ? t.toDate() : new Date(o.syncedAt as number);
  }
  const rawErr = o.exportError;
  const exportError =
    rawErr == null || rawErr === ''
      ? null
      : typeof rawErr === 'string'
        ? rawErr
        : String(rawErr);

  // profitPlus2k8.warehouseStock: { [co_sub]: number | { stock_act, stock_com, stock_lle?, stock_des? } }
  let warehouseStock: WarehouseStockMap | undefined = undefined;
  const rawWh = o.warehouseStock;
  if (rawWh && typeof rawWh === 'object' && !Array.isArray(rawWh)) {
    const norm = normalizeWarehouseStockMap(rawWh);
    warehouseStock = Object.keys(norm).length > 0 ? norm : undefined;
  }
  return {
    fields: (fields && typeof fields === 'object' ? fields : {}) as ProfitPlus2k8ProductIntegration['fields'],
    syncedAt,
    exportError,
    ...(warehouseStock ? { warehouseStock } : {}),
  };
}

function parseFirestoreDate(value: unknown): Date | undefined {
  if (value == null) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }
  if (typeof value === 'object') {
    const t = value as { toDate?: () => Date; seconds?: number };
    if (typeof t.toDate === 'function') {
      const d = t.toDate();
      return Number.isNaN(d.getTime()) ? undefined : d;
    }
    if (typeof t.seconds === 'number') {
      return new Date(t.seconds * 1000);
    }
  }
  const d = new Date(value as string | number);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function parseNumericFieldStats(raw: unknown): ProductNumericFieldStats | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const stats: ProductNumericFieldStats = {
    min: typeof o.min === 'number' ? o.min : undefined,
    max: typeof o.max === 'number' ? o.max : undefined,
    avg: typeof o.avg === 'number' ? o.avg : undefined,
    previous: typeof o.previous === 'number' ? o.previous : undefined,
    changeCount: typeof o.changeCount === 'number' ? o.changeCount : undefined,
    lastChangedAt: parseFirestoreDate(o.lastChangedAt),
  };
  return Object.values(stats).some(v => v !== undefined) ? stats : undefined;
}

function parseMarginStats(raw: unknown): ProductMarginStats | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const stats: ProductMarginStats = {
    currentPercent:
      typeof o.currentPercent === 'number' ? o.currentPercent : undefined,
    minPercent: typeof o.minPercent === 'number' ? o.minPercent : undefined,
    maxPercent: typeof o.maxPercent === 'number' ? o.maxPercent : undefined,
    lastComputedAt: parseFirestoreDate(o.lastComputedAt),
  };
  return Object.values(stats).some(v => v !== undefined) ? stats : undefined;
}

function parseSalesStats(raw: unknown): ProductSalesStats | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const stats: ProductSalesStats = {
    totalUnitsSold:
      typeof o.totalUnitsSold === 'number' ? o.totalUnitsSold : undefined,
    totalRevenue: typeof o.totalRevenue === 'number' ? o.totalRevenue : undefined,
    avgSoldPrice: typeof o.avgSoldPrice === 'number' ? o.avgSoldPrice : undefined,
    minSoldUnitPrice:
      typeof o.minSoldUnitPrice === 'number' ? o.minSoldUnitPrice : undefined,
    maxSoldUnitPrice:
      typeof o.maxSoldUnitPrice === 'number' ? o.maxSoldUnitPrice : undefined,
    orderCount: typeof o.orderCount === 'number' ? o.orderCount : undefined,
    lastSoldAt: parseFirestoreDate(o.lastSoldAt),
  };
  return Object.values(stats).some(v => v !== undefined) ? stats : undefined;
}

/** Normalize product.stats from Firestore (nested Timestamps). */
function parseProductStats(raw: unknown): ProductStats | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const stats: ProductStats = {
    price: parseNumericFieldStats(o.price),
    cost: parseNumericFieldStats(o.cost),
    margin: parseMarginStats(o.margin),
    sales: parseSalesStats(o.sales),
    availability: parseNumericFieldStats(o.availability),
    updatedAt: parseFirestoreDate(o.updatedAt),
  };
  const hasData = Boolean(
    stats.price ||
      stats.cost ||
      stats.margin ||
      stats.sales ||
      stats.availability ||
      stats.updatedAt
  );
  return hasData ? stats : undefined;
}

/** Map a Firestore product document to the admin `Product` model (used by load + realtime). */
export function productFromFirestoreData(
  docId: string,
  data: DocumentData
): Product {
  return {
    id: docId,
    code: data.code || '',
    reference: data.reference || '',
    name: data.name || '',
    taxId: data.taxId || undefined,
    specifications: data.specifications || '',
    department: data.department || '',
    subDepartment: data.subDepartment || '',
    brand: data.brand || undefined,
    availability: data.availability || 0,
    price: data.price || 0,
    measurement: data.measurement || '',
    batch: data.batch || '',
    size: data.size || '',
    color: data.color || '',
    packagingQuantity: data.packagingQuantity
      ? String(data.packagingQuantity)
      : '',
    minSalesQuantity: data.minSalesQuantity || 1,
    description: data.description || '',
    cost: data.cost || 0,
    special: data.special || false,
    active: data.active !== false,
    productType: data.productType || 'physical',
    trackInventory: data.trackInventory !== false,
    barcode: data.barcode || undefined,
    alternateCodes: Array.isArray(data.alternateCodes)
      ? (data.alternateCodes as string[]).map(String).filter(Boolean)
      : undefined,
    unitOfMeasure: data.unitOfMeasure || undefined,
    purchaseUnitOfMeasure: data.purchaseUnitOfMeasure || undefined,
    unitsPerPackage:
      data.unitsPerPackage != null && data.unitsPerPackage !== ''
        ? Number(data.unitsPerPackage)
        : undefined,
    saleIncrement:
      data.saleIncrement != null && data.saleIncrement !== ''
        ? Number(data.saleIncrement)
        : undefined,
    maxSalesQuantity:
      data.maxSalesQuantity != null && data.maxSalesQuantity !== ''
        ? Number(data.maxSalesQuantity)
        : undefined,
    stockMin:
      data.stockMin != null && data.stockMin !== ''
        ? Number(data.stockMin)
        : undefined,
    stockMax:
      data.stockMax != null && data.stockMax !== ''
        ? Number(data.stockMax)
        : undefined,
    reorderPoint:
      data.reorderPoint != null && data.reorderPoint !== ''
        ? Number(data.reorderPoint)
        : undefined,
    weight:
      data.weight != null && data.weight !== ''
        ? Number(data.weight)
        : undefined,
    weightUnit: data.weightUnit || undefined,
    length:
      data.length != null && data.length !== ''
        ? Number(data.length)
        : undefined,
    width:
      data.width != null && data.width !== ''
        ? Number(data.width)
        : undefined,
    height:
      data.height != null && data.height !== ''
        ? Number(data.height)
        : undefined,
    dimensionUnit: data.dimensionUnit || undefined,
    keywords: data.keywords || [],
    imageUrl: data.imageUrl || '',
    storagePath: data.storagePath || '',
    images: data.images
      ? (data.images as Array<{ url: string; storagePath: string }>).map(
          (img: { url: string; storagePath: string }) => ({
            url: img.url,
            storagePath: img.storagePath,
          })
        )
      : undefined,
    created: parseFirestoreDate(data.created),
    updated: parseFirestoreDate(data.updated),
    profitPlus2k8: parseProfitPlus2k8(data.profitPlus2k8),
    stats: parseProductStats(data.stats),
  };
}

/** Single product fetch (server API — includes fresh stats). */
export async function fetchProductById(
  organizationId: string,
  productId: string
): Promise<Product | null> {
  try {
    const data = await authenticatedHttpClient.get<Record<string, unknown>>(
      `products/${organizationId}/${productId}`
    );
    return productFromApiResponse(data);
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } };
    if (err.response?.status === 404) return null;
    throw new Error(
      extractProductApiError(error, 'Error al obtener el producto')
    );
  }
}

/**
 * Load products with pagination and optional search
 */
export const loadProducts = async (
  organizationId: string,
  searchInput?: string,
  lastProduct?: DocumentSnapshot,
  limitCount: number = 10,
  warehouseId?: string
): Promise<ProductQueryResult> => {
  const useWarehouseApi = Boolean(warehouseId?.trim());
  if (isSelfHosted || useWarehouseApi) {
    const offset = getApiListOffset(lastProduct, 0);
    const params = new URLSearchParams();
    params.set('limit', String(limitCount));
    params.set('offset', String(offset));
    if (searchInput?.trim()) {
      params.set('q', searchInput.trim().toLowerCase());
    }
    if (warehouseId?.trim()) {
      params.set('warehouse_id', warehouseId.trim());
    }
    const rows = await authenticatedHttpClient.get<Product[]>(
      `/products/${organizationId}?${params.toString()}`
    );
    return {
      products: rows,
      hasMore: rows.length >= limitCount,
      lastDoc:
        rows.length >= limitCount
          ? (createApiListCursor(offset + limitCount) as unknown as QueryDocumentSnapshot<DocumentData>)
          : null,
    };
  }

  let baseQuery = collection(
    firestore,
    'organizations',
    organizationId,
    'products'
  );

  let q = query(baseQuery);

  // Add search filter if provided
  if (searchInput && searchInput.trim() !== '') {
    q = query(
      baseQuery,
      where('keywords', 'array-contains', searchInput.toLowerCase())
    );
  }

  // Add ordering by created date
  q = query(q, orderBy('created', 'desc'));

  // Add pagination
  if (lastProduct) {
    q = query(q, startAfter(lastProduct));
  }

  // Add limit
  q = query(q, limit(limitCount));

  const snapshot = await getDocs(q);

  const products: Product[] = [];
  snapshot.forEach(docSnap => {
    products.push(productFromFirestoreData(docSnap.id, docSnap.data()));
  });

  return {
    products,
    lastDoc:
      snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
    hasMore: snapshot.docs.length >= limitCount,
  };
};

/**
 * Update product price
 */
export const updateProductPrice = async (
  organizationId: string,
  productId: string,
  price: number
): Promise<void> => {
  await updateProduct(organizationId, productId, { price });
};

/**
 * Delete a product (server deletes Firestore doc and storage images).
 */
export const deleteProduct = async (
  organizationId: string,
  productId: string
): Promise<void> => {
  try {
    await authenticatedHttpClient.delete(
      `products/${organizationId}/${productId}`
    );
  } catch (error: unknown) {
    throw new Error(extractProductApiError(error, 'Error al eliminar el producto'));
  }
};

/**
 * Mark product as special or remove special status
 */
export const updateProductSpecialStatus = async (
  organizationId: string,
  productId: string,
  isSpecial: boolean
): Promise<void> => {
  await updateProduct(organizationId, productId, { special: isSpecial });
};

/**
 * Update product image URL and storage path
 */
export const updateProductImage = async (
  organizationId: string,
  productId: string,
  imageUrl: string,
  storagePath: string
): Promise<void> => {
  await updateProduct(organizationId, productId, {
    imageUrl,
    storagePath,
  });
};

/**
 * Create a new product (server API — seeds stats, keywords, default tax).
 */
export const createProduct = async (
  organizationId: string,
  data: ProductCreate
): Promise<string> => {
  try {
    const response = await authenticatedHttpClient.post<Record<string, unknown>>(
      `products/${organizationId}`,
      prepareProductCreatePayload(data)
    );
    const id = typeof response.id === 'string' ? response.id : '';
    if (!id) {
      throw new Error('El servidor no devolvió el id del producto creado');
    }
    return id;
  } catch (error: unknown) {
    throw new Error(extractProductApiError(error, 'Error al crear el producto'));
  }
};

/**
 * Update product fields (server API — updates stats when price/cost/availability change).
 */
export const updateProduct = async (
  organizationId: string,
  productId: string,
  data: ProductUpdate
): Promise<void> => {
  try {
    await authenticatedHttpClient.put(
      `products/${organizationId}/${productId}`,
      prepareProductUpdatePayload(data)
    );
  } catch (error: unknown) {
    throw new Error(extractProductApiError(error, 'Error al actualizar el producto'));
  }
};

/**
 * Set tax classification (e.g. Exento) for many products at once.
 * Use after inventory update to re-apply exentos by pattern without manual edits.
 */
export const bulkSetProductTax = async (
  organizationId: string,
  productIds: string[],
  taxId: string
): Promise<{ updated: number; errors: number }> => {
  const data = await authenticatedHttpClient.post<{ updated: number; errors: number }>(
    `products/${organizationId}/bulk-set-tax`,
    { productIds, taxId }
  );
  return data;
};
